import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import { ArrowLeft, Save, Eye, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TINYMCE_API_KEY = process.env.REACT_APP_TINYMCE_API_KEY;

const categories = [
  'General',
  'Community Impact',
  'Humanitarian',
  'Faith & Service',
  'Economic Empowerment',
  'Behavioral Health',
  'Organizational Updates',
];

export default function AdminBlogEditor() {
  const { postId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    featured_image: '',
    category: 'General',
    tags: '',
    seo_title: '',
    seo_description: '',
    is_published: false,
  });

  const isEditing = !!postId;

  useEffect(() => {
    if (isEditing) {
      fetchPost();
    }
  }, [postId]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      // We need to fetch by ID, but our API uses slug
      // For editing, we'll get all posts and find by ID
      const response = await axios.get(`${API_URL}/api/blog/posts`, {
        params: { limit: 100 },
        headers: { Authorization: `Bearer ${token}` }
      });
      const post = response.data.items.find(p => p.id === postId);
      if (post) {
        setFormData({
          title: post.title || '',
          slug: post.slug || '',
          excerpt: post.excerpt || '',
          content: post.content || '',
          featured_image: post.featured_image || '',
          category: post.category || 'General',
          tags: post.tags?.join(', ') || '',
          seo_title: post.seo_title || '',
          seo_description: post.seo_description || '',
          is_published: post.is_published || false,
        });
      }
    } catch (error) {
      toast.error('Failed to load post');
      navigate('/admin/blog');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditorChange = (content) => {
    setFormData(prev => ({ ...prev, content }));
  };

  const handleImageUpload = async (blobInfo, progress) => {
    return new Promise(async (resolve, reject) => {
      const formDataImg = new FormData();
      formDataImg.append('file', blobInfo.blob(), blobInfo.filename());

      try {
        const response = await axios.post(
          `${API_URL}/api/blog/upload-image`,
          formDataImg,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: (e) => {
              progress((e.loaded / e.total) * 100);
            },
          }
        );
        resolve(response.data.location);
      } catch (error) {
        reject({ message: 'Image upload failed', remove: true });
      }
    });
  };

  const handleFeaturedImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formDataImg = new FormData();
    formDataImg.append('file', file);

    try {
      const response = await axios.post(
        `${API_URL}/api/blog/upload-image`,
        formDataImg,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      setFormData(prev => ({ ...prev, featured_image: response.data.location }));
      toast.success('Featured image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    if (!formData.content.trim()) {
      toast.error('Content is required');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
      };

      if (isEditing) {
        await axios.put(
          `${API_URL}/api/blog/posts/${postId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Post updated successfully');
      } else {
        await axios.post(
          `${API_URL}/api/blog/posts`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Post created successfully');
      }
      
      navigate('/admin/blog');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#F15A24]"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="admin-blog-editor">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/admin/blog')}
              className="gap-2"
            >
              <ArrowLeft size={18} />
              Back
            </Button>
            <div>
              <h1 className="font-heading text-2xl font-bold text-[#2D3E55]">
                {isEditing ? 'Edit Post' : 'Create New Post'}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch
                id="publish"
                checked={formData.is_published}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_published: checked }))}
                data-testid="publish-switch"
              />
              <Label htmlFor="publish" className="font-body text-sm">
                {formData.is_published ? 'Published' : 'Draft'}
              </Label>
            </div>
            <Button 
              onClick={handleSubmit}
              disabled={saving}
              className="bg-[#F15A24] hover:bg-[#d64d1e] text-white"
              data-testid="save-post-btn"
            >
              <Save size={18} className="mr-2" />
              {saving ? 'Saving...' : 'Save Post'}
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardContent className="p-6 space-y-4">
                  <div>
                    <Label htmlFor="title">Title *</Label>
                    <Input
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Enter post title"
                      className="mt-2 text-lg"
                      data-testid="post-title-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="slug">URL Slug</Label>
                    <Input
                      id="slug"
                      name="slug"
                      value={formData.slug}
                      onChange={handleChange}
                      placeholder="auto-generated-from-title"
                      className="mt-2"
                      data-testid="post-slug-input"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Content *</CardTitle>
                </CardHeader>
                <CardContent className="p-6 pt-0">
                  <Editor
                    ref={editorRef}
                    apiKey={TINYMCE_API_KEY}
                    value={formData.content}
                    onEditorChange={handleEditorChange}
                    init={{
                      height: 500,
                      menubar: true,
                      plugins: [
                        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',
                        'preview', 'anchor', 'searchreplace', 'visualblocks', 'code',
                        'fullscreen', 'insertdatetime', 'media', 'table', 'help', 'wordcount'
                      ],
                      toolbar: 'undo redo | blocks | bold italic forecolor | ' +
                        'alignleft aligncenter alignright alignjustify | ' +
                        'bullist numlist outdent indent | link image media table | ' +
                        'removeformat | help',
                      images_upload_handler: handleImageUpload,
                      automatic_uploads: true,
                      file_picker_types: 'image',
                      content_style: `
                        body { 
                          font-family: 'Manrope', sans-serif; 
                          font-size: 16px; 
                          line-height: 1.7; 
                          color: #2A2A2A;
                        }
                        h1, h2, h3, h4, h5, h6 { 
                          font-family: 'Playfair Display', serif; 
                          color: #2D3E55;
                        }
                      `,
                    }}
                    data-testid="tinymce-editor"
                  />
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Featured Image</CardTitle>
                </CardHeader>
                <CardContent className="p-6 pt-0">
                  {formData.featured_image ? (
                    <div className="relative">
                      <img 
                        src={formData.featured_image} 
                        alt="Featured" 
                        className="w-full h-40 object-cover rounded-lg"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => setFormData(prev => ({ ...prev, featured_image: '' }))}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <label className="block cursor-pointer">
                      <div className="w-full h-40 border-2 border-dashed border-slate-200 rounded-lg flex flex-col items-center justify-center hover:border-[#F15A24] transition-colors">
                        <ImageIcon size={32} className="text-slate-400 mb-2" />
                        <span className="font-body text-sm text-slate-500">Click to upload</span>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFeaturedImageUpload}
                        className="hidden"
                        data-testid="featured-image-input"
                      />
                    </label>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Post Settings</CardTitle>
                </CardHeader>
                <CardContent className="p-6 pt-0 space-y-4">
                  <div>
                    <Label>Category</Label>
                    <Select 
                      value={formData.category}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                    >
                      <SelectTrigger className="mt-2" data-testid="category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="tags">Tags (comma-separated)</Label>
                    <Input
                      id="tags"
                      name="tags"
                      value={formData.tags}
                      onChange={handleChange}
                      placeholder="faith, community, service"
                      className="mt-2"
                      data-testid="tags-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="excerpt">Excerpt</Label>
                    <Textarea
                      id="excerpt"
                      name="excerpt"
                      value={formData.excerpt}
                      onChange={handleChange}
                      placeholder="Brief summary of the post"
                      rows={3}
                      className="mt-2"
                      data-testid="excerpt-input"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">SEO Settings</CardTitle>
                </CardHeader>
                <CardContent className="p-6 pt-0 space-y-4">
                  <div>
                    <Label htmlFor="seo_title">SEO Title</Label>
                    <Input
                      id="seo_title"
                      name="seo_title"
                      value={formData.seo_title}
                      onChange={handleChange}
                      placeholder="Custom title for search engines"
                      className="mt-2"
                      data-testid="seo-title-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="seo_description">Meta Description</Label>
                    <Textarea
                      id="seo_description"
                      name="seo_description"
                      value={formData.seo_description}
                      onChange={handleChange}
                      placeholder="Description for search results"
                      rows={3}
                      className="mt-2"
                      data-testid="seo-description-input"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}
